'use client'

import './globals.css'
import { Suspense, useState, type CSSProperties } from 'react'
import { usePathname } from 'next/navigation'
import { Funnel_Display, Geologica, JetBrains_Mono, Noto_Sans_JP } from 'next/font/google'
import { Providers } from './providers'
import { Header } from '@/components/layout/Header'
import { Sidebar, SidebarProvider, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { CommandPalette } from '@/components/CommandPalette'
import { StageFollower } from '@/components/presenter/StageFollower'
import { TimePassesBanner } from '@/components/shell/TimePassesBanner'
import { POINTER_GLOW_ENABLED, PointerGlow, VisualPattern } from '@/components/brand'
import { AUTO_NAME, AUTO_SUBLABEL } from '@/lib/navigation'
import { cn } from '@/lib/utils'

// Funnel Display (headings)
const funnel = Funnel_Display({
  subsets: ['latin'],
  variable: '--font-funnel',
  display: 'swap',
})

// Geologica (body)
const geologica = Geologica({
  subsets: ['latin'],
  variable: '--font-geologica',
  display: 'swap',
})

// Noto Sans JP for Japanese agency names (`lang="ja"`, plans/00 5.7). Not preloaded: few pages show it.
const notoJp = Noto_Sans_JP({
  subsets: ['latin'],
  weight: ['400', '500', '600'],
  variable: '--font-noto-jp',
  display: 'swap',
  preload: false,
})

// JetBrains Mono for record ids (WB-101, DLV-0105) and step codes.
const mono = JetBrains_Mono({
  subsets: ['latin'],
  variable: '--font-mono',
  display: 'swap',
})

// Routes that should NOT show the app shell (rail, header)
const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error']

/** First focusable element on every page: hidden until it takes focus, then jumps past the rail (Rule 14). */
function SkipLink() {
  return (
    <a
      href='#main-content'
      className='sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-toast focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
    >
      Skip to main content
    </a>
  )
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()

  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route))

  if (isAuthRoute) {
    return (
      <div className='min-h-screen'>
        <SkipLink />
        <VisualPattern variant='subtle' />
        <main
          id='main-content'
          tabIndex={-1}
          className='relative z-10 flex min-h-screen items-center justify-center p-4 focus:outline-none'
        >
          {children}
        </main>
      </div>
    )
  }

  // The header and the content follow the rail width (open 16rem, collapsed 4rem).
  const railStyle = { '--shell-rail-width': isCollapsed ? '4rem' : '16rem' } as CSSProperties

  return (
    <>
      <SkipLink />
      <VisualPattern variant='subtle' />
      {POINTER_GLOW_ENABLED && <PointerGlow />}

      <Sidebar />
      <MobileSidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <div
        style={railStyle}
        className={cn(
          'min-h-screen',
          // Sticky page toolbars sit below the floating header (top-4 + h-14) with a 12px gap.
          '[--shell-sticky-top:5.25rem]',
          'md:pl-[var(--shell-rail-width)]',
          'transition-all duration-300 ease-out'
        )}
      >
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />
        {/* The story autopilot's "time passes" banner, on every page and the stage window. */}
        <TimePassesBanner />

        <main
          id='main-content'
          tabIndex={-1}
          className='flex min-h-screen flex-col px-4 pb-8 pt-24 focus:outline-none lg:px-8'
        >
          {children}
        </main>
      </div>

      <CommandPalette />
      {/* Inert unless this window is the Presenter's stage window (plans/07). */}
      <Suspense fallback={null}>
        <StageFollower />
      </Suspense>
    </>
  )
}

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en' className='light' suppressHydrationWarning>
      <head>
        <title>{`${AUTO_NAME} · ${AUTO_SUBLABEL}`}</title>
        <meta name='description' content={`${AUTO_NAME} for the ${AUTO_SUBLABEL}`} />
        <link rel='icon' href={`${process.env.NEXT_PUBLIC_BASE_PATH || ''}/supervity-favicon.png`} />
      </head>
      <body
        className={cn(
          'min-h-screen font-sans antialiased',
          'bg-background text-foreground',
          funnel.variable,
          geologica.variable,
          notoJp.variable,
          mono.variable
        )}
      >
        <Providers>
          <SidebarProvider>
            <LayoutContent>{children}</LayoutContent>
          </SidebarProvider>
        </Providers>
      </body>
    </html>
  )
}
